import { cloneDeep, isNumber, isString, toInteger } from 'lodash';
import { v4 as uuid } from 'uuid';
import { checkIsAiText, checkIsPhoto, CoreAPI, getBehavior, getTrackFunc, HoverExtendType } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { isHotKeyPress } from '@/utils/event-kit';
import { getImgInfoAsync, limitMaxImageInfo, loadImageAsync, svg2Png, uploadVectorFile } from '@/utils/file-kit';
import { computerBgRatio, getImgLayerProps } from '@/utils/layer-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import { parseVector } from '@/utils/vector-kit';
import {
    IBackgroundLayerProps,
    IBorderRadiusProps,
    IExportPlugin,
    IImageAiTextExtension,
    IImageEffectProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    LayerType,
    MASK_DEFAULT_IMG,
    MaterialIdsType,
    PluginType,
    StretchOptionType,
} from '@/version-types';
import { IAddLayerArgs, Layer, layerPlugin } from '../layer';
import { getVectorLayerProps } from '../vector-layer/utils';
import imageLayerBehavior from './behavior';
import { defaultImageTransform, getDefaultImageLayerData } from './constants/default-image-data';
import { getImageEffectResultImgSize } from './constants/image-layer-constant';
import { ImageHistory } from './image-history';
import { HistoryType, HistoryType as ImageHistoryType } from './type';
import ImageEffectAction from './utils/image-effect-action';
import { replaceImage, setMask } from './utils/layer';

const DefaultClassicBlurParams: ClassicBlurParams = {
    mModelType: 2, // 需要使用自动模式
    mUpdateEffect: 1, // 每次切换需要更新
    mFocalScale: 0.5, // 范围 0-1
};

type ClassicBlurParams = {
    [key: string]: number;
};

export class ImageLayer extends Layer {
    classicBlurParams: ClassicBlurParams;
    private effectData: {
        effect?: IImageEffectProps | null;
        borderRadius?: IBorderRadiusProps | null;
        opacity?: number;
    } | null = null;

    constructor(editorIdentity: string) {
        super(editorIdentity);
        this.classicBlurParams = cloneDeep(DefaultClassicBlurParams);
    }

    /** 更新图片图层并设置为激活状态 */
    updateLayer(newLayer: ILayerProps, oldLayer?: ILayerProps, pushHistory = true, trackFunc?: string) {
        this.framework.stage.addOrUpdate(newLayer);
        if (pushHistory && oldLayer) {
            this.updateHistory(oldLayer, newLayer, trackFunc);
        }

        this.framework.operate.setActiveLayer(newLayer.id);
    }

    /** 更新图片图层 */
    updateHistory(oldLayer: ILayerProps, newLayer: ILayerProps, trackFunc?: string) {
        this.framework.history.write(oldLayer, newLayer, trackFunc);
    }

    /**
     * 设置图层蒙版
     *
     * @param id - 图层 ID
     * @param mask - 蒙版属性；`mask.url` 蒙版链接；`mask.materialId` 蒙版素材 ID
     */
    async setMask(id: string, { url, materialId }: { url: string; materialId: string }) {
        const layer = this.framework.getLayer(id, true);
        const oldLayer = cloneDeep(layer);
        if (!layer) return;
        await setMask(layer, url, materialId, this.framework.identity);
        this.framework.stage.addOrUpdate(layer);
        this.framework.history.write(oldLayer, 'mask');
        this.framework.operate.setActiveLayer(layer.id);
    }

    /**
     * 设置图片特效渲染状态
     *
     * @param id - 图层 ID
     * @param open - 是否开启
     */
    showImageEffectRenderState(id: string, open: boolean) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        layer.__renderEnhanceCanvas = false;
        layer.__renderGLCanvas = open;
        this.framework.stage.addOrUpdate(layer);
    }

    /**
     * 添加 修改图片特效
     *
     * @param newLayer - 新图层
     * @param oldLayer - 旧图层
     * @param statTrackKey - 可选；统计 Key
     * @param isEmpty - 可选；是否为空；默认值为 `false`
     */
    async changeImageEffectAttr(
        newLayer: ILayerProps,
        oldLayer?: ILayerProps,
        statTrackKey?: string,
        isEmpty = false,
        lastLayer?: ILayerProps,
    ) {
        if (!this.framework.getLayer(newLayer.id)) return;
        const trackFunc = statTrackKey ? getTrackFunc(this.identity, newLayer, statTrackKey) : undefined;
        if (oldLayer) {
            this.framework.stage.addOrUpdate({ ...newLayer, __renderGLCanvas: false });
            this.framework.operate.setActiveLayer(newLayer.id);
            this.framework.history.write(
                { ...oldLayer, __renderGLCanvas: false },
                { ...newLayer, __renderGLCanvas: false },
                trackFunc,
            );
        } else {
            if (
                !newLayer.__renderGLCanvas ||
                getImageEffectResultImgSize(newLayer.data as IImageLayerProps) !==
                    getImageEffectResultImgSize(lastLayer?.data as IImageLayerProps)
            ) {
                newLayer.__renderGLCanvas = true;
                const layerResultImage = await this.getLayerImageAsync(newLayer, true, false, true, false);
                if (layerResultImage) {
                    await ImageEffectAction.glRender(newLayer, layerResultImage);
                }
            } else {
                ImageEffectAction.processRender(newLayer, statTrackKey === 'borderRadius');
            }
            const data = newLayer.data as IImageLayerProps;

            if (!isEmpty && data.effect) {
                const url = await ImageEffectAction.getScreenshotUrl(newLayer);
                if (url) {
                    data.effect.screenshotUrl = url;
                }
            }

            this.framework.stage.addOrUpdate(newLayer);
            this.framework.operate.setActiveLayer(newLayer.id);

            if (trackFunc) {
                this.framework.history.pushFunc(trackFunc);
            }
        }
    }

    async getEffectScreenshotUrl(layer: ILayerProps) {
        if (ImageEffectAction.isEmptyEffect(layer.data as IImageLayerProps)) {
            return '';
        }
        const url = await ImageEffectAction.getScreenshotUrl(layer);
        return url as string;
    }

    /**
     * 运行图片效果并获取截图链接地址
     *
     * @param layer - 图层属性
     */
    async runEffectAndGetUrl(layer: ILayerProps) {
        if (ImageEffectAction.isEmptyEffect(layer.data as IImageLayerProps)) {
            return '';
        }

        const layerResultImage = await this.getLayerImageAsync(layer, true, false, true, false);
        if (!layerResultImage) {
            return '';
        }
        await ImageEffectAction.glRender(layer, layerResultImage);
        const url = await ImageEffectAction.getScreenshotUrl(layer);
        return url as string;
    }

    /**
     * 获取图层结果图片
     *
     * @param layer - 图层
     * @param globalAlpha - 可选；全局模糊度
     * @param needScaleCanvas - 可选；是否需要缩放画布
     * @param noNeedBorderRadius - 可选；是否不需要边框圆角
     */
    async getLayerImageAsync(
        layer: ILayerProps,
        globalAlpha?: boolean,
        needScaleCanvas?: boolean,
        noNeedBorderRadius?: boolean,
        useLayerOpacity?: boolean,
    ) {
        return getBehavior(this.identity, layer)?.getLayerImageAsync?.(
            layer,
            globalAlpha,
            needScaleCanvas,
            noNeedBorderRadius,
            useLayerOpacity,
        );
    }

    /**
     * 渲染经典模糊
     *
     * @param layer - 图层属性
     * @param alpha - 模糊度
     */
    async renderClassicBlur(layer: ILayerProps, alpha: number) {
        if (alpha !== undefined) {
            this.classicBlurParams.mFocalScale = alpha / 100;
        }

        ImageEffectAction.renderClassicBlur(layer, this.classicBlurParams);

        this.framework.stage.addOrUpdate({
            ...cloneDeep(layer),
            __renderEnhanceCanvas: true,
        });
    }

    /**
     * 改变经典模糊模糊度
     *
     * @param alpha - 模糊度
     */
    changeClassicBlurAlpha(alpha: number) {
        ImageEffectAction.changeClassicBlurAlpha(alpha, this.classicBlurParams);
    }

    /**
     * 重置经典模糊参数
     */
    resetClassicBlurParams() {
        this.classicBlurParams = cloneDeep(DefaultClassicBlurParams);
    }

    /**
     * 渲染玻璃模糊
     *
     * @param layer - 图层属性
     */
    async renderImageGlassBlur(layer: ILayerProps) {
        await ImageEffectAction.renderImageGlassBlur(layer);

        this.framework.stage.addOrUpdate({
            ...cloneDeep(layer),
            __renderEnhanceCanvas: true,
        });
    }

    async processImgAndReplaceUrl(
        url: string,
        layerId: string,
        imageWidth?: number,
        imageHeight?: number,
        naturalWidth?: number,
        naturalHeight?: number,
        pushHistory = true,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        useNewSize = false,
    ) {
        const layer = this.framework.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();

        let newImageWidth = 0;
        let newImageHeight = 0;

        // 如果没有传入图片信息就自己获取
        if (imageWidth === undefined || imageHeight === undefined) {
            // 获取图片信息
            const { width: imageWidth, height: imageHeight } = await getImgInfoAsync(url);
            newImageWidth = imageWidth;
            newImageHeight = imageHeight;
        } else {
            newImageWidth = imageWidth;
            newImageHeight = imageHeight;
        }

        // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
        const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
        newImageWidth = limitResult.naturalWidth;
        newImageHeight = limitResult.naturalHeight;
        url = limitResult.url;

        let newLayer = cloneDeep(layer);

        const { width: layerWidth, height: layerHeight } = newLayer;
        const imgInfo = newLayer?.data as IImageLayerProps;
        // let { stretchOption } = imgInfo;

        // 每次都要重置imageTransform
        imgInfo.naturalWidth = naturalWidth || newImageWidth;
        imgInfo.naturalHeight = naturalHeight || newImageHeight;
        imgInfo.url = url;
        imgInfo.cutoutMaskURL = '';
        newLayer.width = newImageWidth;
        newLayer.height = newImageHeight;
        ImageEffectAction.closeGLRender(newLayer);
        const imageTransform: IImageTransform = {
            ...cloneDeep(defaultImageTransform),
            widthRatio: 1,
            heightRatio: 1,
        };

        if (!useNewSize) {
            newLayer.left = layer.left - (newLayer.width - layer.width) / 2;
            newLayer.top = layer.top - (newLayer.height - layer.height) / 2;
        }

        imgInfo.imageTransform = imageTransform;

        newLayer = {
            ...newLayer,
            data: {
                ...imgInfo,
            },
        };

        newLayer = Object.assign(newLayer, extendOldLayerData);

        // 替换图片后更新投影
        await this.redrawEffect(newLayer, false);

        // 重新绘制图层
        this.framework.stage.addOrUpdate({
            ...cloneDeep(newLayer),
        });

        const oldInfo = layer;
        const newInfo = newLayer;
        this.framework.history.write(
            { ...oldInfo, __renderGLCanvas: false },
            { ...newInfo, __renderGLCanvas: false },
            getTrackFunc(this.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
        );
        this.framework.operate.setActiveLayer(newLayer.id);
        return { oldInfo, newInfo };
    }

    /**
     *
     * 添加图片图层
     * @param {string} url 图片地址
     * @param {number} width 图片宽度
     * @param {number} height 图片高度
     * @param {number} left 图片left位置
     * @param {number} top  图片top位置
     * @param {string} [materialId] 素材id
     *
     * @param {boolean} [fitWithStage] 调整尺寸适应舞台
     * @param {boolean} [pushHistory] 是否推入历史
     *
     */
    async addImageLayer(
        {
            url,
            width: naturalWidth,
            height: naturalHeight,
            top,
            left,
            materialId,
            isPhoto,
            isAILayer,
            opacity,
            imageTransform,
            aiTextExtension,
            fontMaterialId,
            byMousePosition = false,
            fixedCenter = false,
            isTempLayer = false,
            lockRatio = true,
        }: {
            url: string;
            width?: number;
            height?: number;
            top?: number;
            left?: number;
            materialId?: string | Record<MaterialIdsType, string[]>;
            isPhoto?: boolean;
            isAILayer?: boolean;
            opacity?: number;
            imageTransform?: IImageTransform;
            aiTextExtension?: IImageAiTextExtension;
            fontMaterialId?: number;
            byMousePosition?: boolean;
            fixedCenter?: boolean;
            isTempLayer?: boolean;
            lockRatio?: boolean;
        },
        fitWithStage = true,
        pushHistory = true,
    ) {
        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();

        let width = naturalWidth;
        let height = naturalHeight;

        if (fitWithStage || !width || !height) {
            const stageRatio = stageWidth / stageHeight;
            if (!naturalWidth || !naturalHeight) {
                const { width, height } = await loadImageAsync(url);
                naturalWidth = width;
                naturalHeight = height;
            }

            // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
            const limitResult = limitMaxImageInfo(stageWidth, url, naturalWidth, naturalHeight);
            naturalWidth = limitResult.naturalWidth;
            naturalHeight = limitResult.naturalHeight;
            url = limitResult.url;

            const ratio = naturalWidth / naturalHeight;

            if (stageRatio > ratio) {
                height = toInteger(stageHeight / 2);
                width = toInteger(naturalWidth / (naturalHeight / height));
            } else {
                width = toInteger(stageWidth / 2);
                height = toInteger(naturalHeight / (naturalWidth / width));
            }
        }

        if (fixedCenter && !byMousePosition) {
            const centerPoint = {
                x: toInteger(stageWidth / 2),
                y: toInteger(stageHeight / 2),
            };
            left = centerPoint.x - width / 2;
            top = centerPoint.y - height / 2;
        }

        // 获取图片图层默认数据
        const imageLayerData = getDefaultImageLayerData(
            url,
            width,
            height,
            left ?? 0,
            top ?? 0,
            materialId,
            naturalWidth,
            naturalHeight,
            isPhoto,
        );

        // 获取图片图层数据
        const layer = getImgLayerProps(imageLayerData);

        let finalData: ILayerProps | IAddLayerArgs = layer;

        layer.shine = false;
        if (opacity) {
            layer.opacity = opacity;
        }
        if (imageTransform) {
            (layer.data as IImageLayerProps).imageTransform = imageTransform;
        }

        // 添加花字属性
        if (aiTextExtension) {
            (layer.data as IImageLayerProps).aiTextExtension = aiTextExtension;
            if (fontMaterialId) {
                layer.materialIds = layer.materialIds || {};
                layer.materialIds[MaterialIdsType.FONT_FAMILY] = [`${fontMaterialId}`];
            }
            layer.isAILayer = true;
        }

        if (isAILayer) {
            layer.isAILayer = true;
        }

        if (isTempLayer) {
            layer.__isTempLayer = true;
        }

        layer.extraInfo = {
            ...layer.extraInfo,
            lockRatio,
        };

        if (!pushHistory) {
            finalData = {
                layer: layer,
                isActiveNewLayer: true,
                isWriteHistory: pushHistory,
            };
        }

        if (byMousePosition) {
            this.addByMousePosition(finalData);
        } else if (top !== undefined && left !== undefined) {
            this.add(finalData);
        } else {
            this.addByDiagonal(finalData);
        }

        return layer;
    }

    async addImageLayerKeepSize(option: {
        url: string;
        width: number;
        height: number;
        naturalWidth?: number;
        naturalHeight?: number;
        top?: number;
        left?: number;
        materialId?: string | Record<MaterialIdsType, string[]>;
        imageTransform?: IImageTransform;
        isPhoto?: boolean;
        lockRatio?: boolean;
        opacity?: number;
    }) {
        const { url, width, height, imageTransform, top, left, materialId, isPhoto, lockRatio, opacity } = option;

        let { naturalWidth, naturalHeight } = option;

        if (!naturalHeight || !naturalWidth) {
            const img = await loadImageAsync(url);
            naturalHeight = img.height;
            naturalWidth = img.width;
        }

        // 获取图片图层默认数据
        const imageLayerData = getDefaultImageLayerData(
            url,
            width,
            height,
            left ?? 0,
            top ?? 0,
            materialId,
            naturalWidth,
            naturalHeight,
            isPhoto,
        );

        const layer = getImgLayerProps(imageLayerData);

        layer.extraInfo = {
            ...layer.extraInfo,
            lockRatio,
        };
        layer.shine = false;
        if (opacity) {
            layer.opacity = opacity;
        }
        if (imageTransform) {
            (layer.data as IImageLayerProps).imageTransform = imageTransform;
        }
        const finalData = {
            layer: layer,
            isActiveNewLayer: true,
            isWriteHistory: true,
        };

        if (top !== undefined && left !== undefined) {
            this.add(finalData);
        } else {
            this.addByDiagonal(finalData);
        }

        return layer;
    }

    /**
     *
     * 替换图片图层
     *
     * @param url - 图片地址
     * @param layerId - 图层 ID
     * @param imageWidth - 可选；替换的图片宽度
     * @param imageHeight - 可选；替换的图片高度
     * @param pushHistory - 可选；是否添加到历史记录中；默认值为 `true`
     * @param materialIds - 可选；素材 ID 列表
     * @param prompt - 可选；提示语
     * @param trackFunc - 可选；统计 Key
     * @param isAi - 可选；是否为 AI 图层；默认值为 `false`
     * @param extendOldLayerData - 可选；扩展的旧图层数据
     */
    async replaceImage(
        url: string,
        layerId: string,
        imageWidth?: number,
        imageHeight?: number,
        pushHistory = true,
        materialIds: Record<string, string[]> = {},
        prompt = '',
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        resetSize = false,
    ) {
        const layer = this.framework.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();

        let newLayer = await replaceImage(
            layer,
            url,
            stageWidth,
            prompt,
            materialIds,
            imageWidth,
            imageHeight,
            resetSize,
        );

        newLayer = Object.assign(newLayer, extendOldLayerData);
        const oldInfo = layer;
        const newInfo = newLayer;

        // 替换图片后更新投影
        await this.redrawEffect(newLayer, false);

        this.framework.stage.addOrUpdate(newLayer);

        if (pushHistory) {
            this.framework.history.write(
                layer,
                trackFunc || getTrackFunc(this.framework.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
            );
            this.framework.operate.setActiveLayer(layer.id);
        }
        // setIsLoadingImage({ isLoading: false, id: layerId });
        return { oldInfo, newInfo };
    }

    /**
     * 处理 AI 修改图片
     *
     * @param url - 图片地址
     * @param layerId - 图层 ID
     * @param pushHistory - 可选；是否推入历史；默认值为 `true`
     * @param trackFunc - 可选；统计 Key
     * @param isAi - 可选；是否为 AI 图层；默认值为 `false`
     * @param extendOldLayerData - 可选；扩展旧图层数据
     */
    async processAIModifyImage(
        url: string,
        layerId: string,
        pushHistory = true,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
    ) {
        const layer = this.framework.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        let newLayer = cloneDeep(layer);

        const imgInfo = newLayer?.data as IImageLayerProps;

        imgInfo.url = url;
        imgInfo.cutoutMaskURL = '';

        ImageEffectAction.closeGLRender(newLayer);

        newLayer = {
            ...newLayer,
            data: {
                ...imgInfo,
            },
        };

        newLayer = Object.assign(newLayer, extendOldLayerData);
        const oldInfo = layer;
        const newInfo = newLayer;

        // 替换图片后更新投影
        await this.redrawEffect(newLayer, false);

        this.framework.stage.addOrUpdate(newLayer);

        if (pushHistory) {
            this.framework.history.write(
                layer,
                trackFunc || getTrackFunc(this.framework.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
            );
            this.framework.operate.setActiveLayer(layer.id);
        }

        return { oldInfo, newInfo };
    }

    async processAIExpendImage(
        url: string,
        layerId: string,
        imageWidth: number,
        imageHeight: number,
        pushHistory = true,
        newWidth: number,
        newHeight: number,
        ratio: string,
        trackFunc?: string,
        isAi = false,
        extendOldLayerData?: Partial<ILayerProps>,
        useNewSize = false,
    ) {
        const layer = this.framework.getLayer(layerId, true);

        if (!layer) {
            return;
        }

        if (isAi) {
            layer.isAILayer = true;
        }

        const { width: stageWidth, height: stageHeight } = this.framework.stage.getActiveStageSize();

        let newImageWidth = imageWidth;
        let newImageHeight = imageHeight;

        if (!useNewSize) {
            // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
            const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
            newImageWidth = limitResult.naturalWidth;
            newImageHeight = limitResult.naturalHeight;
            url = limitResult.url;
        }

        let newLayer = cloneDeep(layer);

        const imgInfo = newLayer?.data as IImageLayerProps;

        // 每次都要重置imageTransform
        imgInfo.naturalWidth = newImageWidth;
        imgInfo.naturalHeight = newImageHeight;
        imgInfo.url = url;
        imgInfo.cutoutMaskURL = '';
        ImageEffectAction.closeGLRender(newLayer);
        let imageTransform: IImageTransform = {
            ...cloneDeep(defaultImageTransform),
            widthRatio: 1,
            heightRatio: 1,
        };
        imageTransform = Object.assign(
            imageTransform,
            computerBgRatio({
                imgWidth: newImageWidth,
                imgHeight: newImageHeight,
                stageHeight: newHeight,
                stageWidth: newWidth,
            }),
        );

        imgInfo.imageTransform = imageTransform;

        newLayer = {
            ...newLayer,
            data: {
                ...imgInfo,
            },
        };
        const oldWidth = newLayer.width;
        const oldHeight = newLayer.height;

        // let [ratioWidth, ratioHeight] = [oldWidth, oldHeight];

        // if (ratio !== '0') {
        // [ratioWidth, ratioHeight] = ratio.split(':').map((str) => parseInt(str));
        // }

        newLayer.width = newWidth;
        newLayer.height = newHeight;

        if (!useNewSize) {
            newLayer.left = newLayer.left - (newLayer.width - oldWidth) / 2;
            newLayer.top = newLayer.top - (newLayer.height - oldHeight) / 2;
        } else {
            newLayer.left = 0;
            newLayer.top = 0;
        }

        newLayer = Object.assign(newLayer, extendOldLayerData);

        const oldInfo = layer;
        const newInfo = newLayer;

        if (layer.layerType !== LayerType.MAIN_LAYER) {
            // 替换图片后更新投影
            await this.redrawEffect(newLayer, false);
            this.framework.stage.addOrUpdate(cloneDeep(newLayer));
            if (pushHistory) {
                this.framework.history.write(
                    cloneDeep(layer),
                    trackFunc || getTrackFunc(this.framework.identity, newLayer, isAi ? 'aiReplace' : 'replace'),
                );
                this.framework.operate.setActiveLayer(layer.id);
            }
        }

        return { oldInfo, newInfo };
    }

    /**
     * 有mask的突出，快捷删除，清空图片素材，保留容器
     *
     * @param layerId - 图层 ID
     * @param trackFunc - 埋点方法
     */
    async clearImage(layerId: string, trackFunc?: string) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer) {
            return;
        }
        const newLayer = cloneDeep(oldLayer);
        newLayer.materialIds = newLayer.materialIds || {};
        newLayer.materialIds[MaterialIdsType.FUNC_LIST] = [];
        newLayer.materialIds[MaterialIdsType.IMAGE_MATERIAL] = [];
        newLayer.materialIds[MaterialIdsType.PHOTO_MATERIAL] = [];
        newLayer.materialIds[MaterialIdsType.FILTER] = [];
        (newLayer.data as IImageLayerProps).url = MASK_DEFAULT_IMG().url;
        (newLayer.data as IImageLayerProps).effect = null;
        ImageEffectAction.closeGLRender(newLayer);
        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write(oldLayer, newLayer, trackFunc);
        this.framework.operate.setActiveLayer(layerId);
    }

    /**
     * 图片转背景
     *
     * @param layerId - 图层 ID
     * @param deleteImage - 可选；是否删除图片；默认值为 `false`
     * @param materialIds - 可选；素材 ID 列表
     */
    convertImageToBackground(layerId: string, deleteImage = true, materialIds?: Record<string, string[]>) {
        const layer = this.framework.getLayer(layerId, true);
        const newLayer = cloneDeep(layer);
        if (!newLayer) return;
        const coreApi = CoreAPI(this.framework.identity);
        const bgLayer = coreApi.getBackgroundLayer(this.framework.stage.getActiveOriginIndex());
        const newBgLayer = cloneDeep(bgLayer)!;
        /**
         * step1: 把当前图片图层的url 设置成 背景的url
         * step2: 删除当前图片图层
         * step3: 写入”组合历史“数据
         */
        const { data: imgData } = newLayer;
        const { url, naturalWidth, naturalHeight } = imgData as IImageLayerProps;
        const { widthRatio, heightRatio } = computerBgRatio({
            imgWidth: naturalWidth,
            imgHeight: naturalHeight,
            stageHeight: newBgLayer.height,
            stageWidth: newBgLayer.width,
        });
        const bgData = newBgLayer.data as IBackgroundLayerProps;
        const newBgData = {
            ...bgData,
            naturalHeight,
            naturalWidth,
            url: base64Kit.getRemoteUrl(url),
            imageTransform: {
                ...(imgData as IImageLayerProps).imageTransform,
                widthRatio,
                heightRatio,
            },
            stretchOption: StretchOptionType.cover,
        };
        newBgLayer.data = newBgData;
        newBgLayer.materialIds = cloneDeep(newLayer.materialIds);
        // 清空蒙版素材
        delete newBgLayer.materialIds?.[MaterialIdsType.MASK];
        newBgLayer.opacity = newLayer.opacity;

        // 追加素材
        if (materialIds) {
            Object.entries(materialIds).forEach(([key, value]) => {
                if (value.length) newBgLayer.materialIds?.[key]?.push(...value);
            });
        }

        this.framework.operate.delLayer(layerId);
        this.framework.operate.delActiveLayer();
        this.framework.stage.addOrUpdate(newBgLayer);
        const originIndex = this.framework.tryGetLayerOriginIndex(layer.id);
        if (!isNumber(originIndex)) return;

        this.framework.history.write({
            type: ImageHistoryType.CONVERT_IMAGE_TO_BACKGROUND,
            data: {
                item: {
                    oldLayer: layer,
                    layerIndex: originIndex,
                    newBgLayer: cloneDeep(newBgLayer),
                    oldBgLayer: cloneDeep(bgLayer),
                },
            },
        });
    }

    checkIsPhoto(args: string | ILayerProps) {
        return checkIsPhoto(this.identity, isString(args) ? this.framework.getLayer(args) : args);
    }

    checkIsAiText(args: string | ILayerProps) {
        return checkIsAiText(this.identity, isString(args) ? this.framework.getLayer(args) : args);
    }

    /**
     * 扩展图片并更换背景
     *
     * @param newLayer - 新图层
     * @param oldLayer - 旧图层
     * @param oldBgLayer - 旧背景图层
     * @param newBgLayer - 新背景图层
     */
    expendImageAndChangeBg(
        newLayer: ILayerProps,
        oldLayer: ILayerProps,
        oldBgLayer: ILayerProps,
        newBgLayer: ILayerProps,
    ) {
        this.framework.history.write({
            type: ImageHistoryType.EXPEND_IMAGE_AND_CHANGE_BG,
            data: {
                item: {
                    newLayer: cloneDeep(newLayer),
                    oldLayer: cloneDeep(oldLayer),
                    oldBgLayer: cloneDeep(oldBgLayer),
                    newBgLayer: cloneDeep(newBgLayer),
                },
            },
        });
    }

    setFilter(
        data: { oldLayer: ILayerProps; newLayer: ILayerProps; originLayer: ILayerProps; isMainFilter: boolean },
        trackFunc?: string,
    ) {
        this.framework.stage.addOrUpdate(data.newLayer);
        this.framework.history.write({
            type: HistoryType.APPLY_FILTER,
            data: {
                item: {
                    oldLayer: cloneDeep(data.oldLayer),
                    newLayer: cloneDeep(data.newLayer),
                    originLayer: cloneDeep(data.originLayer),
                    isMainFilter: data.isMainFilter,
                    type: HistoryType.APPLY_FILTER,
                },
            },
            trackFunc,
        });
    }

    async replaceImageLayerByVectorFile(layer: ILayerProps, file: File, fillType?: string) {
        const { svgEl } = await svg2Png(file);
        const svgFile = await uploadFileToObs(file);
        const { text, info } = await parseVector(svgEl);
        const imageUrl = await uploadVectorFile(text);
        layer.id = uuid();
        const svgInfo = {
            image: imageUrl.url,
            info,
            file: svgFile.url,
        };

        let newWidth = layer.width;
        let newHeight = layer.height;

        if (fillType === 'full') {
            const newImageWidth = info.naturalWidth;
            const newImageHeight = info.naturalHeight;

            if (newImageWidth / layer.width > newImageHeight / layer.height) {
                newWidth = layer.width;
                newHeight = (layer.width * newImageHeight) / newImageWidth;
            } else {
                newHeight = layer.height;
                newWidth = (layer.height * newImageWidth) / newImageHeight;
            }

            layer.left = layer.left + layer.width / 2 - newWidth / 2;
            layer.top = layer.top + layer.height / 2 - newHeight / 2;
            layer.width = newWidth;
            layer.height = newHeight;
        }

        const vectorLayer = getVectorLayerProps(
            {
                url: svgInfo.image,
                left: layer.left,
                top: layer.top,
                width: newWidth,
                height: newHeight,
            },
            info,
        );
        return vectorLayer;
    }

    async replaceImageLayerByImageFile(layer: ILayerProps, file: File, fillType = 'cover') {
        const { url: remoteUrl } = await uploadFileToObs(file);

        const { width: newImageWidth, height: newImageHeight } = await getImgInfoAsync(remoteUrl);

        const imgInfo = layer.data as IImageLayerProps;
        // let { stretchOption } = imgInfo;

        // 每次都要重置imageTransform
        imgInfo.naturalWidth = newImageWidth;
        imgInfo.naturalHeight = newImageHeight;
        imgInfo.url = remoteUrl;
        imgInfo.cutoutMaskURL = '';

        let imageTransform: IImageTransform = {
            ...cloneDeep(defaultImageTransform),
            widthRatio: 1,
            heightRatio: 1,
        };

        if (fillType === 'cover') {
            // 如果是自由模式还要计算下宽高比
            imageTransform = Object.assign(
                imageTransform,
                computerBgRatio({
                    imgWidth: newImageWidth,
                    imgHeight: newImageHeight,
                    stageHeight: layer.height,
                    stageWidth: layer.width,
                }),
            );
        } else {
            let newWidth = layer.width;
            let newHeight = layer.height;

            if (newImageWidth / layer.width > newImageHeight / layer.height) {
                newWidth = layer.width;
                newHeight = (layer.width * newImageHeight) / newImageWidth;
            } else {
                newHeight = layer.height;
                newWidth = (layer.height * newImageWidth) / newImageHeight;
            }

            layer.left = layer.left + layer.width / 2 - newWidth / 2;
            layer.top = layer.top + layer.height / 2 - newHeight / 2;
            layer.width = newWidth;
            layer.height = newHeight;
        }

        imgInfo.imageTransform = imageTransform;
        layer.isAILayer = false;

        layer.data = imgInfo;
        layer.id = uuid();

        if (layer.data.effect) {
            const url = await this.runEffectAndGetUrl(layer);

            if (url) {
                const resultUrl = await base64Kit.getRemoteUrlAsync(url);
                layer.data.effect.screenshotUrl = resultUrl;
            }
        }
    }

    /**
     * 复制图片图层的效果
     *
     * @param layerId - 图层ID
     **/
    extractEffectData(layerId: string) {
        const layer = this.framework.getLayer(layerId, true);
        const layerData = layer.data as IImageLayerProps;
        this.effectData = cloneDeep({
            effect: layerData.effect,
            borderRadius: layerData.borderRadius,
            opacity: layer.opacity,
        });
    }

    /**
     * 开启图片特效笔刷
     *
     * @param layerId - 图层ID
     * @param tip - 提示
     **/
    setImageBrush(layerId: string, tip?: string) {
        const layer = this.framework.getLayer(layerId);
        if (!layer) return;
        this.framework.operate.hoverExtend.set({
            cursor: '--cursor-brush',
            layerId,
            type: HoverExtendType.IMAGE_BRUSH,
            cursorTip: tip,
            apply: async (targetLayerId) => {
                if (!this.effectData || targetLayerId === layerId) return;
                const { effect, borderRadius, opacity } = this.effectData;
                const layer = this.framework.getLayer(targetLayerId);
                if (!layer || layer.layerType !== LayerType.IMAGE_LAYER) return;
                layer.opacity = opacity ?? 1;
                const layerData = layer.data as IImageLayerProps;
                const oldLayer = cloneDeep(layer);
                layerData.borderRadius = cloneDeep(borderRadius);
                layerData.effect = cloneDeep(effect);

                await this.redrawEffect(layer, false);

                this.framework.stage.addOrUpdate(cloneDeep(layer));
                this.framework.history.write(oldLayer, layer);

                if (!isHotKeyPress()) {
                    this.framework.operate.setActiveLayer(layer.id);
                    this.effectData = null;
                }
            },
        });
    }

    /**
     * 重新绘制特效图片
     *
     * @param layer - 图层
     * @param updateLayer- 可选，是否更新图层触发绘制，默认为true
     **/
    async redrawEffect(layer: ILayerProps, updateLayer = true) {
        try {
            if (
                layer.layerType !== LayerType.IMAGE_LAYER ||
                ImageEffectAction.isEmptyEffect(layer.data as IImageLayerProps)
            ) {
                return;
            }
            const layerData = layer.data as IImageLayerProps;

            const runEffect = async () => {
                layerData.effect!.screenshotUrl = '';
                this.framework.stage.addOrUpdate(cloneDeep(layer));
                const layerResultImage = await getBehavior(this.identity, layer)?.getLayerImageAsync?.(
                    layer,
                    false,
                    false,
                    true,
                    false,
                );
                if (layerResultImage) {
                    await ImageEffectAction.glRender(layer, layerResultImage);
                    const url = await ImageEffectAction.getScreenshotUrl(layer);
                    if (url) {
                        layerData.effect!.screenshotUrl = url;
                    }
                }
            };

            if (!ImageEffectAction.isEmptyEffect(layerData)) await runEffect();
            if (updateLayer) this.framework.stage.addOrUpdate(cloneDeep(layer));
        } catch (e) {
            console.error(e);
        }
    }
}

/**
 * 图片图层插件
 */
export const imageLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.IMAGE_LAYER,
    createInstance({ identity }) {
        return [{ key: 'ImageLayer', value: new ImageLayer(identity) }];
    },
    createBehavior() {
        return imageLayerBehavior;
    },
    createHistory({ identity }) {
        return [new ImageHistory(identity)];
    },
};
